import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react'
import { getNlsString } from '../../nls/nlsRuntime'
import type { ChipSize } from './Chip'
import { ChipRow } from './ChipRow'
import { Tip } from './Hover'
import { countVisibleChips } from './chipGroupHelpers'
import './chip.css'

export type ChipGroupItem = {
  id: string
  label: string
  node: ReactNode
}

export interface ChipGroupProps {
  layout: 'wrap' | 'line' | 'scroll'
  items: ChipGroupItem[]
  ariaLabel?: string
  size?: ChipSize
  className?: string
}

export interface MoreChipProps {
  hiddenLabels: string[]
  size?: ChipSize
}

export function MoreChip({ hiddenLabels, size = 'md' }: MoreChipProps) {
  const count = hiddenLabels.length
  const moreText = getNlsString('NLSCommonChipGroupMore', { count: String(count) })
  const moreAria = getNlsString('NLSCommonChipGroupMoreAria', {
    count: String(count),
    labels: hiddenLabels.join(', '),
  })

  return (
    <Tip label={hiddenLabels.join(', ')} speed="delayed">
      <span
        tabIndex={0}
        className={`dsChip dsChip--more dsChip--${size} moreChip`}
        aria-label={moreAria}
      >
        {moreText}
      </span>
    </Tip>
  )
}

export function ChipGroup({
  layout,
  items,
  ariaLabel,
  size = 'md',
  className,
}: ChipGroupProps) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const [visibleCount, setVisibleCount] = useState<number>(items.length)

  useEffect(() => {
    if (layout !== 'line') return
    if (typeof ResizeObserver === 'undefined') return

    const update = () => {
      const host = hostRef.current
      const measure = measureRef.current
      if (!host || !measure) return

      const available = host.clientWidth || host.getBoundingClientRect().width
      const count = items.length
      const widths: number[] = []
      for (let i = 0; i < count; i++) {
        const el = measure.children[i]
        const w = el instanceof HTMLElement ? el.offsetWidth || el.getBoundingClientRect().width : 0
        widths.push(w)
      }
      const moreEl = measure.children[count]
      const moreWidth =
        moreEl instanceof HTMLElement ? moreEl.offsetWidth || moreEl.getBoundingClientRect().width : 0

      const k = countVisibleChips(widths, available, moreWidth, 6)
      setVisibleCount(k)
    }

    update()

    const observer = new ResizeObserver(update)
    if (hostRef.current) observer.observe(hostRef.current)
    if (measureRef.current) observer.observe(measureRef.current)
    return () => observer.disconnect()
  }, [layout, items, size])

  if (layout === 'wrap') {
    return (
      <div
        role="group"
        aria-label={ariaLabel}
        className={['dsChipGroup', 'dsChipGroup--wrap', className].filter(Boolean).join(' ')}
      >
        {items.map((item) => (
          <Fragment key={item.id}>{item.node}</Fragment>
        ))}
      </div>
    )
  }

  if (layout === 'scroll') {
    return (
      <ChipRow
        ariaLabel={ariaLabel}
        className={['dsChipGroup', 'dsChipGroup--scroll', className].filter(Boolean).join(' ')}
      >
        {items.map((item) => (
          <Fragment key={item.id}>{item.node}</Fragment>
        ))}
      </ChipRow>
    )
  }

  const k = typeof ResizeObserver === 'undefined' ? items.length : visibleCount
  const visibleItems = items.slice(0, k)
  const hiddenItems = items.slice(k)
  const showMore = hiddenItems.length > 0
  const sampleHidden = items.length > 0 ? items.map((it) => it.label) : ['sample']

  return (
    <div
      ref={hostRef}
      role="group"
      aria-label={ariaLabel}
      className={['dsChipGroup', 'dsChipGroup--line', className].filter(Boolean).join(' ')}
    >
      <div
        ref={measureRef}
        className="dsChipGroupMeasure"
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: '0 auto auto 0',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {items.map((item) => (
          <Fragment key={`measure-${item.id}`}>{item.node}</Fragment>
        ))}
        <MoreChip hiddenLabels={sampleHidden} size={size} />
      </div>
      {visibleItems.map((item) => (
        <Fragment key={item.id}>{item.node}</Fragment>
      ))}
      {showMore && <MoreChip hiddenLabels={hiddenItems.map((it) => it.label)} size={size} />}
    </div>
  )
}
